import dayjs, { type ConfigType } from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import i18next from 'i18next';

dayjs.extend(localizedFormat);

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

dayjs.extend(duration);

/*
 * Configure RelativeTime plugin
 */

type RelativeTimeOptions = Parameters<typeof relativeTime>[0];
const strictThresholds: RelativeTimeOptions['thresholds'] = [
  { l: 's', r: 1 },
  { l: 'm', r: 1 },
  { l: 'mm', r: 59, d: 'minute' },
  { l: 'h', r: 1 },
  { l: 'hh', r: 23, d: 'hour' },
  { l: 'd', r: 1 },
  { l: 'dd', r: 29, d: 'day' },
  { l: 'M', r: 1 },
  { l: 'MM', r: 11, d: 'month' },
  { l: 'y', r: 1 },
  { l: 'yy', d: 'year' }
];
const relativeTimeConfig: RelativeTimeOptions = {
  thresholds: strictThresholds
};
dayjs.extend(relativeTime, relativeTimeConfig);

/*
 * Configure UpdateLocale plugin
 */

dayjs.extend(updateLocale);

// Todo: should these texts be localized?
const texts = {
  s: 'gerade',
  m: ['eine Min.', 'einer Min.'],
  mm: '%d Min.',
  h: ['eine Std.', 'einer Std.'],
  hh: '%d Std.',
  d: ['ein Tag', 'einem Tag'],
  dd: ['%d Tage', '%d Tagen'],
  M: ['ein Monat', 'einem Monat'],
  MM: ['%d Monate', '%d Monaten'],
  y: ['ein Jahr', 'einem Jahr'],
  yy: ['%d Jahre', '%d Jahren']
};

const relativeTimeFormatter = (
  number: number,
  withoutSuffix: boolean,
  key: keyof typeof texts
): string => {
  let l = texts[key];
  if (Array.isArray(l)) {
    l = l[withoutSuffix ? 0 : 1];
  }
  return l.replace('%d', String(number));
};

dayjs.updateLocale('de', {
  relativeTime: {
    future: 'in %s',
    past: 'vor %s',
    s: relativeTimeFormatter,
    m: relativeTimeFormatter,
    mm: relativeTimeFormatter,
    h: relativeTimeFormatter,
    hh: relativeTimeFormatter,
    d: relativeTimeFormatter,
    dd: relativeTimeFormatter,
    M: relativeTimeFormatter,
    MM: relativeTimeFormatter,
    y: relativeTimeFormatter,
    yy: relativeTimeFormatter
  }
});

/*
 * Update locale when i18next locale changes
 */

i18next.on('languageChanged', (lng) => {
  dayjs.locale(lng);
});

const TODAY = dayjs().startOf('day').toDate();
const TOMORROW = dayjs().add(1, 'day').startOf('day').toDate();

export { dayjs, TODAY, TOMORROW, type ConfigType };
